/**
 * <TutorSessionFeedCard> — une carte du bac « À décider » de la session avec Sam (molecule).
 *
 * **Une seule anatomie, aujourd'hui et demain**, allégée au round 3 (Wajih, 02/11) :
 *  1. **qui et quoi** — la photo de l'élève, le signe du signal posé dessus (la main, le chrono, le
 *     fantôme…, l'emoji de l'ancienne carte), une phrase au prénom en gras, le temps écoulé à droite ;
 *  2. **la preuve**, seulement quand elle dit quelque chose de plus : demain, l'extrait entendu (`quote`,
 *     deux lignes au plus) ;
 *  3. **l'action, une seule** (l'ancien `getEffectiveCta`) : pleine quand elle demande le masteur, en
 *     lien quand elle dit seulement ce qui se passe (`passive` : « … aide Léa »).
 *
 * **La priorité se voit** : une entrée `urgent` (la main, le silence) est teintée d'alerte, avec son
 * liseré ; l'escalade (quatre minutes sans réponse) la passe au rouge et le dit. Les autres sont des
 * cartes blanches ; celles où le champion s'en occupe (`low`) restent à plat.
 *
 * **La signature du champion** : il agit (son nom au pied), ou il propose (`sam="proposes"` : la carte
 * en pointillés, une proposition n'est pas encore un fait). Pas d'aurore sur une carte : dans la session,
 * elle n'est qu'en tête, quand le champion est avec l'élève.
 *
 * La croix écarte une entrée qui le permet (`dismissible`) ; sur une main levée, elle **baisse la main**
 * (l'OK de l'ancienne pastille, le seul geste d'un cours individuel, #2876) et le dit : sur un appareil qui survole (une souris),
 * elle ne paraît qu'au survol ou au clavier ; au doigt — une tablette dans un navigateur comprise —,
 * elle est toujours là.
 */
import React, { useState } from "react"
import { Platform, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import {
    type UITutorFeedItem,
    type UITutorFeedPriority,
    type UITutorFeedSignal,
} from "../../../ports/primary/ui/types/tutorLive"
import { CHAMPIONS, ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { inkOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { Card } from "../Card"
import { type AvatarSign, SignedAvatar } from "../SignedAvatar"

export type TutorSessionFeedCardProps = {
    item: UITutorFeedItem
    onAction: (actionId: string) => void
    /** La croix, sur une carte `dismissible`. Absente : pas de croix. */
    onDismiss?: () => void
    /** L'action qui part : son bouton tourne. */
    pendingActionId?: string
    /** Le champion qui aide en séance (le monde de jeu de l'espace) : sa tête et son prénom signent la carte. Absent : pas de signature. */
    helper?: ChampionName
    testID?: string
}

const TEXTS = {
    escalated: "Sans réponse — à toi de jouer",
    validWhile: (condition: string) => `Valable ${condition}`,
    proposes: (name: string) => `${name} propose`,
    dismiss: "Écarter",
    lowerHand: (firstname: string) => `Baisser la main de ${firstname}`,
}

/** Le signe de chaque signal, posé sur la photo (les emoji de l'ancienne carte). */
const SIGNAL_ICON: Record<UITutorFeedSignal, IconName> = {
    hand_raised: "hand-paper",
    silence: "stopwatch",
    out_of_app: "ghost",
    exercise_done: "check",
    auto_activity: "compass",
    sam_ongoing: "comment-dots",
    sam_summary: "comment-dots",
    sam_proposal: "lightbulb",
    sam_whisper: "lightbulb",
    sam_nudge: "volume-up",
}

type PriorityFace = {
    background?: SemanticColorKey
    rail?: SemanticColorKey
    sign: SemanticColorKey
    ink: SemanticColorKey
}

/**
 * Ce que la priorité change : le fond, le liseré, la pastille du signe. L'encre d'une pastille pleine est
 * celle de son aplat (`inkOnFill` : sur l'orange plein, l'encre foncée ; le blanc n'y tient pas 3:1).
 */
const PRIORITY_FACE: Record<UITutorFeedPriority | "escalated", PriorityFace> = {
    escalated: { background: "dangerLight", rail: "danger", sign: "danger", ink: inkOnFill("danger") },
    urgent: { background: "warningLight", rail: "warning", sign: "warning", ink: inkOnFill("warning") },
    normal: { sign: "surface-subtle", ink: "text-strong" },
    low: { sign: "surface-subtle", ink: "text-muted" },
}

/** Le signe d'une entrée, et sa pastille : la carte le pose sur la photo, le fil replié aussi. */
export function tutorFeedSign(item: UITutorFeedItem): AvatarSign {
    const face = PRIORITY_FACE[item.escalated === true ? "escalated" : item.priority]
    return { icon: SIGNAL_ICON[item.signal], color: face.sign, ink: face.ink }
}

/** La photo ou l'initiale de l'élève : petite, la phrase est l'information. */
const FEED_AVATAR_SIZE = 28
const SAM_SIZE = 18
/**
 * Un pointeur qui survole (une souris) : la croix attend le survol. Au doigt — le natif, ou une tablette
 * dans un navigateur, qui n'a pas de survol —, elle reste. Lu une fois : l'appareil ne change pas.
 */
const HOVER_ONLY =
    Platform.OS === "web" && typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches === true

export function TutorSessionFeedCard(props: TutorSessionFeedCardProps) {
    const { item, onAction, onDismiss, pendingActionId, helper, testID } = props
    const proposes = item.sam === "proposes"
    // État d'interface : la carte survolée, ou sa croix au clavier, montre la croix (le web).
    const [hovered, setHovered] = useState(false)
    const [crossFocused, setCrossFocused] = useState(false)
    const dismissible = item.dismissible === true && onDismiss != null
    // Sur une main levée, la croix devient la coche qui la baisse : toujours là, c'est un geste.
    const lowersHand = item.signal === "hand_raised"
    const crossVisible = lowersHand || !HOVER_ONLY || hovered || crossFocused
    const face = PRIORITY_FACE[item.escalated === true ? "escalated" : item.priority]
    const action = item.action

    const card = (
        <Card
            p="3"
            leftBorderColor={proposes ? undefined : face.rail}
            background={face.background}
            elevation={proposes || item.priority === "low" ? "none" : "card"}
            style={proposes ? styles.proposal : undefined}
            testID={testID}
        >
            <Stack gap="2">
                <Row gap="2" align="start">
                    <SignedAvatar
                        uri={item.student.avatarUrl}
                        name={item.student.firstname}
                        size={FEED_AVATAR_SIZE}
                        sign={tutorFeedSign(item)}
                    />
                    <Text size="text-sm" color="text-primary" numberOfLines={2} style={styles.sentence}>
                        <Text size="text-sm" weight="bold" color="text-strong">
                            {item.student.firstname}
                        </Text>{" "}
                        {item.text}
                    </Text>
                    <Text size="text-xs" color="text-muted">
                        {item.elapsed}
                    </Text>
                    {dismissible && (
                        <View
                            style={styles.cross(crossVisible)}
                            onFocus={() => setCrossFocused(true)}
                            onBlur={() => setCrossFocused(false)}
                        >
                            <IconButton
                                iconName={lowersHand ? "check" : "times"}
                                appearance="transparent"
                                iconColor="text-muted"
                                size={24}
                                iconSize={14}
                                onPress={onDismiss}
                                accessibilityLabel={
                                    lowersHand
                                        ? TEXTS.lowerHand(item.student.firstname)
                                        : `${TEXTS.dismiss} : ${item.student.firstname} ${item.text}`
                                }
                            />
                        </View>
                    )}
                </Row>

                {item.escalated === true && (
                    <Text size="text-xs" weight="bold" color="danger-strong">
                        {TEXTS.escalated}
                    </Text>
                )}

                {item.validWhile != null && (
                    <Text size="text-xs" color="text-muted">
                        {TEXTS.validWhile(item.validWhile)}
                    </Text>
                )}

                {item.quote != null && (
                    <Text size="text-sm" color="text-primary" numberOfLines={2} style={styles.quote}>
                        « {item.quote} »
                    </Text>
                )}
                {(action != null || item.sam != null) && (
                    <Row gap="3" align="center" style={styles.actions}>
                        {action != null && action.passive !== true && (
                            <Button
                                label={action.label}
                                size="sm"
                                color={item.escalated === true ? "danger" : undefined}
                                loading={action.id === pendingActionId}
                                onPress={() => onAction(action.id)}
                            />
                        )}
                        {action?.passive === true && (
                            <TextButton
                                label={action.label}
                                size="sm"
                                loading={action.id === pendingActionId}
                                onPress={() => onAction(action.id)}
                            />
                        )}
                        {item.sam != null && helper != null && (
                            <Row gap="1" align="center" style={styles.signature}>
                                <ChampionAvatar name={helper} size={SAM_SIZE} />
                                <Text size="text-xs" weight="bold" color="text-muted">
                                    {proposes ? TEXTS.proposes(CHAMPIONS[helper].label) : CHAMPIONS[helper].label}
                                </Text>
                            </Row>
                        )}
                    </Row>
                )}
            </Stack>
        </Card>
    )
    // Seule une carte qui a une croix à cacher suit le survol.
    if (!HOVER_ONLY || !dismissible) return card
    return (
        <Pressable
            onHoverIn={() => setHovered(true)}
            onHoverOut={() => setHovered(false)}
            accessible={false}
            focusable={false}
        >
            {card}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    sentence: { flex: 1, minWidth: 0 },
    quote: { fontStyle: "italic" as const },
    actions: { flexWrap: "wrap", rowGap: theme.spacing["2"] },
    signature: { marginLeft: "auto" },
    cross: (visible: boolean) => ({ opacity: visible ? 1 : 0 }),
    /** Une proposition : la carte en pointillés, sans ombre — pas encore un fait. */
    proposal: {
        borderWidth: 1.5,
        borderStyle: "dashed" as const,
        borderColor: theme.colors["border"],
    },
}))
